import { Field, useFormikContext } from 'formik'

import { Box } from 'app/components/design-system-components'
import { InputField } from 'app/components/design-system-components/inputs'
import { SelectField } from 'app/components/design-system-components/inputs/Select/Select'
import {
  Heading,
  Text,
} from 'app/components/design-system-components/typography'
import { formFields } from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/form.utils'
import {
  StyledFieldWrapper,
  StyledFlexBox,
} from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/styled-form-component'
import type { RentalHistoryFormValues } from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/types'
import {
  australianStates,
  countryOptions,
  getStateDisplayValue,
  isAddressAustralian,
} from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/utils/addressUtils'

interface AddressSectionProps {
  values: RentalHistoryFormValues
}

/**
 * Address section component - Modernized with design system components
 * Uses FieldWrapper with InputField/SelectField for proper Formik integration
 */
export const AddressSection = ({ values }: AddressSectionProps) => {
  const { setFieldValue } = useFormikContext<RentalHistoryFormValues>()

  const handleCountryChange = (
    selectedCountry: any,
    currentState: any,
    fieldName: string,
  ) => {
    // Set the new country value
    setFieldValue(fieldName, selectedCountry)

    // Check if switching from Australia to non-Australia
    const willBeAustralian =
      selectedCountry && isAddressAustralian({ country: selectedCountry })

    // If switching to non-Australia and current state is a StateOption object, transform it to string
    if (
      !willBeAustralian &&
      typeof currentState === 'object' &&
      currentState !== null
    ) {
      const stateString = getStateDisplayValue(currentState)
      if (stateString) {
        setFieldValue('address.state', stateString)
      }
    }
  }

  return (
    <Box mb={7}>
      <Heading level={3} displayingLevel={3} mb={3}>
        Address
      </Heading>
      <Text mb={7}>Add the applicant’s previous address.</Text>

      {/* Row 1: Unit Number + Street Number */}
      <StyledFlexBox gap={3} mb={7}>
        <Box flex={1}>
          <StyledFieldWrapper
            id={formFields['address.unitNumber']}
            name={formFields['address.unitNumber']}
            label="Unit Number (optional)"
          >
            <InputField
              name={formFields['address.unitNumber']}
              inputProps={{ placeholder: 'e.g. 1' }}
            />
          </StyledFieldWrapper>
        </Box>
        <Box flex={1}>
          <StyledFieldWrapper
            id={formFields['address.streetNumber']}
            name={formFields['address.streetNumber']}
            label="Street Number"
            required
          >
            <InputField
              name={formFields['address.streetNumber']}
              inputProps={{ placeholder: 'e.g. 123' }}
            />
          </StyledFieldWrapper>
        </Box>
      </StyledFlexBox>

      {/* Row 2: Street Name + Suburb */}
      <StyledFlexBox gap={3} mb={7}>
        <Box flex={1}>
          <StyledFieldWrapper
            id={formFields['address.streetName']}
            name={formFields['address.streetName']}
            label="Street Name"
            required
          >
            <InputField
              name={formFields['address.streetName']}
              inputProps={{ placeholder: 'e.g. Example St' }}
            />
          </StyledFieldWrapper>
        </Box>
        <Box flex={1}>
          <StyledFieldWrapper
            id={formFields['address.suburb']}
            name={formFields['address.suburb']}
            label="Suburb"
            required
          >
            <InputField
              name={formFields['address.suburb']}
              inputProps={{ placeholder: 'e.g. Sydney' }}
            />
          </StyledFieldWrapper>
        </Box>
      </StyledFlexBox>

      {/* Row 3: State + Postcode */}
      <StyledFlexBox gap={3} mb={7}>
        <Box flex={1}>
          <StyledFieldWrapper
            id={formFields['address.state']}
            name={formFields['address.state']}
            label={
              values.address.country && isAddressAustralian(values.address)
                ? 'State'
                : 'State/Province/Region'
            }
            required
          >
            {values.address.country && isAddressAustralian(values.address) ? (
              <Field name={formFields['address.state']}>
                {({ field }: any) => (
                  <SelectField
                    {...field}
                    options={australianStates}
                    label="Select State"
                  />
                )}
              </Field>
            ) : (
              <InputField
                name={formFields['address.state']}
                inputProps={{ placeholder: 'e.g. NSW' }}
              />
            )}
          </StyledFieldWrapper>
        </Box>
        <Box flex={1}>
          <StyledFieldWrapper
            id={formFields['address.postcode']}
            name={formFields['address.postcode']}
            label="Postcode"
            required
          >
            <InputField
              name={formFields['address.postcode']}
              inputProps={{ placeholder: 'e.g. 2000' }}
            />
          </StyledFieldWrapper>
        </Box>
      </StyledFlexBox>

      {/* Row 4: Country */}
      <StyledFlexBox gap={3} mb={7}>
        <Box flex={1}>
          <StyledFieldWrapper
            id={formFields['address.country']}
            name={formFields['address.country']}
            label="Country"
            required
          >
            <Field name={formFields['address.country']}>
              {({ field }: any) => (
                <SelectField
                  {...field}
                  options={countryOptions}
                  label="Select Country"
                  optionsHeight="300px"
                  changeHandler={(selectedCountry: any) =>
                    handleCountryChange(
                      selectedCountry,
                      values.address.state,
                      field.name,
                    )
                  }
                />
              )}
            </Field>
          </StyledFieldWrapper>
        </Box>
        <Box flex={1}></Box>
      </StyledFlexBox>
    </Box>
  )
}
